import { ObjectType } from '$app-web/types';

export function isObject(value: unknown) {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof RegExp) &&
        !(value instanceof Error) &&
        !(value instanceof Date) &&
        !(value instanceof Map) &&
        !(value instanceof Set) &&
        !(value instanceof File)
    );
}

// Accepts either an already-parsed object or a JSON string, and returns it only when it is a plain object.
export function parseJsonObject(value: unknown): ObjectType | null {
    if (isObject(value)) {
        return value as ObjectType;
    }
    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }
    try {
        const parsed = JSON.parse(value);
        return isObject(parsed) ? (parsed as ObjectType) : null;
    } catch {
        return null;
    }
}

export function pickObjectKeys<T extends object, Key extends keyof T>(data: T, keys: Array<Key>): Pick<T, Key> {
    return keys.reduce(
        (newObject, key) => {
            if (Object.hasOwn(data, key) && data[key] != null) {
                return { ...newObject, [key]: data[key] };
            }
            return newObject;
        },
        {} as Pick<T, Key>
    );
}

export function omitObjectKeys<T extends object, Key extends keyof T>(data: T, keys: Array<Key>): Omit<T, Key> {
    return Object.entries(data).reduce(
        (newObject, [key, value]) => {
            if (!keys.includes(key as Key)) {
                return { ...newObject, [key]: value };
            }
            return newObject;
        },
        {} as Omit<T, Key>
    );
}

export function hasObjectKeys<T extends ObjectType>(data: T, ...keys: Array<string | number>): boolean {
    return keys.every((key) => key in data);
}

export function groupObjectBy<
    T extends ObjectType,
    Key extends {
        [K in keyof T]: T[K] extends string | number ? K : never;
    }[keyof T],
    Result extends ObjectType<Array<T>>,
>(data: Array<T>, key: Key): Result {
    return data.reduce((newObject, item) => {
        const groupKey = item[key] as string | number;
        if (hasObjectKeys(newObject, groupKey)) {
            const prevValue = newObject[groupKey];
            return { ...newObject, [groupKey]: [...prevValue, item] } as Result;
        }
        return { ...newObject, [groupKey]: [item] } as Result;
    }, {} as Result);
}

export function mergeObjectsDeeply<T extends object>(target: T, ...sources: Array<object>): T {
    if (!sources.length) {
        return target;
    }
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        Object.keys(source as object).forEach((key) => {
            if (isObject((source as Record<string, unknown>)[key])) {
                if (!target[key as keyof T]) {
                    Object.assign(target, { [key]: {} });
                }
                mergeObjectsDeeply(target[key as keyof T] as object, (source as Record<string, unknown>)[key] as object);
            } else {
                Object.assign(target, { [key]: (source as Record<string, unknown>)[key] });
            }
        });
    }

    return mergeObjectsDeeply(target, ...sources);
}

export function getObjectLength(data: ObjectType): number {
    return Object.keys(data).length;
}
